"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useState } from "react";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import Link from "next/link";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropPart } from "@/generated/models/ApiDropPart";
import DropPartMarkdownWithPropLogger from "@/components/drops/view/part/DropPartMarkdownWithPropLogger";
import WaveDropTime from "./time/WaveDropTime";
import { getWaveRoute } from "@/helpers/navigation.helpers";
import Image from "next/image";
import { resolveIpfsUrlSync } from "@/components/ipfs/IPFSContext";
interface WaveDropQuoteProps {
  readonly drop: ApiDrop | null;
  readonly partId: number;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly marketplaceImageOnly?: boolean | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
}
const WaveDropQuote: React.FC<WaveDropQuoteProps> = t0 => {
  const $ = _c(68);
  const {
    drop,
    partId,
    onQuoteClick,
    embedPath,
    quotePath,
    marketplaceImageOnly,
    embedDepth,
    maxEmbedDepth
  } = t0;
  const [quotedPart, setQuotedPart] = useState(null);
  let t1;
  if ($[0] !== drop || $[1] !== partId) {
    t1 = () => {
      if (!drop) {
        return;
      }
      const part_0 = drop.parts.find(part => part.part_id === partId);
      if (!part_0) {
        return;
      }
      setQuotedPart(part_0);
    };
    $[0] = drop;
    $[1] = partId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== drop) {
    t2 = [drop];
    $[3] = drop;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  useEffect(t1, t2);
  let t3;
  if ($[5] !== drop) {
    t3 = () => {
      if (!drop) {
        return <div className="tw-h-full tw-w-full tw-max-w-full tw-animate-pulse tw-overflow-hidden tw-rounded-md tw-bg-iron-900" />;
      }
      const resolvedPfp = drop.author.pfp ? resolveIpfsUrlSync(drop.author.pfp) : null;
      if (resolvedPfp) {
        return <div className="tw-relative tw-h-full tw-w-full"><Image src={resolvedPfp} alt={`${drop.author.handle}'s profile picture`} fill={true} sizes="24px" className="tw-rounded-md tw-bg-transparent tw-object-cover" /></div>;
      }
      return <div className="tw-h-full tw-w-full tw-max-w-full tw-overflow-hidden tw-rounded-md tw-bg-iron-900" />;
    };
    $[5] = drop;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const renderProfilePicture = t3;
  let t4;
  if ($[7] !== drop || $[8] !== onQuoteClick) {
    t4 = () => {
      if (drop?.wave.id && drop?.id) {
        onQuoteClick(drop);
      }
    };
    $[7] = drop;
    $[8] = onQuoteClick;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const goToQuoteDrop = t4;
  let t5;
  bb0: {
    if (!drop) {
      t5 = "";
      break bb0;
    }
    const waveDetails = drop.wave as unknown as {
      chat?: {
        scope?: {
          group?: {
            is_direct_message?: boolean | undefined;
          } | undefined;
        } | undefined;
      } | undefined;
    };
    const isDirectMessage = waveDetails?.chat?.scope?.group?.is_direct_message ?? false;
    let t6;
    if ($[10] !== drop.wave.id || $[11] !== isDirectMessage) {
      t6 = getWaveRoute({
        waveId: drop.wave.id,
        isDirectMessage,
        isApp: false
      });
      $[10] = drop.wave.id;
      $[11] = isDirectMessage;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    t5 = t6;
  }
  const waveHref = t5;
  let path;
  if ($[13] !== drop || $[14] !== quotePath) {
    path = quotePath ? [...quotePath] : [];
    if (drop?.wave.id) {
      const currentQuoteKey = `${drop.wave.id}:${drop.serial_no}`;
      if (!path.includes(currentQuoteKey)) {
        path.push(currentQuoteKey);
      }
    }
    $[13] = drop;
    $[14] = quotePath;
    $[15] = path;
  } else {
    path = $[15];
  }
  const effectiveQuotePath = path;
  let t6;
  let t7;
  if ($[16] !== goToQuoteDrop) {
    t6 = e => {
      e.stopPropagation();
      goToQuoteDrop();
    };
    t7 = e_0 => {
      if (e_0.key === "Enter" || e_0.key === " ") {
        e_0.preventDefault();
        e_0.stopPropagation();
        goToQuoteDrop();
      }
    };
    $[16] = goToQuoteDrop;
    $[17] = t6;
    $[18] = t7;
  } else {
    t6 = $[17];
    t7 = $[18];
  }
  let t8;
  if ($[19] !== renderProfilePicture) {
    t8 = <div className="tw-relative tw-h-6 tw-w-6 tw-flex-shrink-0 tw-rounded-md tw-bg-iron-900"><div className="tw-h-full tw-w-full tw-rounded-md">{renderProfilePicture()}</div></div>;
    $[19] = renderProfilePicture;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== drop) {
    t9 = !!drop && <UserCICAndLevel level={drop.author.level} size={UserCICAndLevelSize.SMALL} />;
    $[21] = drop;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const t10 = `/${drop?.author.handle}`;
  const t11 = drop?.author.handle;
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <p className="tw-mb-0 tw-text-md tw-font-semibold tw-leading-none"><Link href={t10} className="tw-text-iron-200 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-500">{t11}</Link></p>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t9) {
    t13 = <div className="tw-flex tw-items-center tw-gap-x-2">{t9}{t12}</div>;
    $[26] = t12;
    $[27] = t9;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== drop) {
    t14 = !!drop && <><div className="tw-size-[3px] tw-flex-shrink-0 tw-rounded-full tw-bg-iron-600" /><WaveDropTime timestamp={drop.created_at} /></>;
    $[29] = drop;
    $[30] = t14;
  } else {
    t14 = $[30];
  }
  let t15;
  if ($[31] !== t13 || $[32] !== t14) {
    t15 = <div className="tw-flex tw-items-center tw-gap-x-2">{t13}{t14}</div>;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== drop || $[35] !== waveHref) {
    t16 = drop && waveHref && <Link href={waveHref} className="tw-leading-0 -tw-mt-1 tw-text-[11px] tw-text-iron-500 tw-no-underline tw-transition tw-duration-300 tw-ease-out hover:tw-text-iron-300">{drop.wave.name}</Link>;
    $[34] = drop;
    $[35] = waveHref;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t16) {
    t17 = <div>{t16}</div>;
    $[37] = t16;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  const t18 = quotedPart?.content ?? "";
  let t19;
  if ($[39] !== drop?.mentioned_users) {
    t19 = drop?.mentioned_users ?? [];
    $[39] = drop?.mentioned_users;
    $[40] = t19;
  } else {
    t19 = $[40];
  }
  let t20;
  if ($[41] !== drop?.mentioned_waves) {
    t20 = drop?.mentioned_waves ?? [];
    $[41] = drop?.mentioned_waves;
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] !== drop?.referenced_nfts) {
    t21 = drop?.referenced_nfts ?? [];
    $[43] = drop?.referenced_nfts;
    $[44] = t21;
  } else {
    t21 = $[44];
  }
  const t22 = drop?.id;
  let t23;
  if ($[45] !== effectiveQuotePath || $[46] !== embedDepth || $[47] !== embedPath || $[48] !== marketplaceImageOnly || $[49] !== maxEmbedDepth || $[50] !== onQuoteClick || $[51] !== t18 || $[52] !== t19 || $[53] !== t20 || $[54] !== t21 || $[55] !== t22) {
    t23 = <div className="tw-mt-0.5"><DropPartMarkdownWithPropLogger partContent={t18} mentionedUsers={t19} mentionedWaves={t20} referencedNfts={t21} textSize="sm" onQuoteClick={onQuoteClick} currentDropId={t22} marketplaceImageOnly={marketplaceImageOnly} embedPath={embedPath} quotePath={effectiveQuotePath} embedDepth={embedDepth} maxEmbedDepth={maxEmbedDepth} /></div>;
    $[45] = effectiveQuotePath;
    $[46] = embedDepth;
    $[47] = embedPath;
    $[48] = marketplaceImageOnly;
    $[49] = maxEmbedDepth;
    $[50] = onQuoteClick;
    $[51] = t18;
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
    $[55] = t22;
    $[56] = t23;
  } else {
    t23 = $[56];
  }
  let t24;
  if ($[57] !== t15 || $[58] !== t17 || $[59] !== t23) {
    t24 = <div className="tw-flex tw-w-full tw-flex-col">{t15}{t17}{t23}</div>;
    $[57] = t15;
    $[58] = t17;
    $[59] = t23;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  let t25;
  if ($[61] !== t24 || $[62] !== t8) {
    t25 = <div className="tw-group tw-relative tw-flex tw-w-full tw-flex-col"><div className="tw-flex tw-gap-x-2">{t8}{t24}</div></div>;
    $[61] = t24;
    $[62] = t8;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t25 || $[65] !== t6 || $[66] !== t7) {
    t26 = <div className="tw-mt-1 tw-cursor-pointer tw-rounded-xl tw-bg-iron-950 tw-px-3 tw-py-3 tw-ring-1 tw-ring-inset tw-ring-iron-800" onClick={t6} onKeyDown={t7} role="button" tabIndex={0}>{t25}</div>;
    $[64] = t25;
    $[65] = t6;
    $[66] = t7;
    $[67] = t26;
  } else {
    t26 = $[67];
  }
  return t26;
};
export default WaveDropQuote;